/*
 * custom-store-upload.js — Prinstey store-native uploads
 *
 * Sends customer files from the storefront straight to our storage as a
 * presigned multipart upload, coordinated by the store-upload API:
 *
 *   POST {endpoint}/init      -> submission + one presigned URL per part
 *   PUT  <part url>           -> each slice of each file (XHR, for progress)
 *   POST {endpoint}/parts     -> fresh URLs when S3 answers 403 (expired)
 *   POST {endpoint}/complete  -> assemble, verify, get the admin links back
 *   POST {endpoint}/abort     -> sendBeacon on cancel or failure
 *
 * Used by sections/custom-print-quote.liquid (one big 3D file per cart line)
 * and sections/custom-contact-form.liquid (project attachments). Plain ES2017,
 * no dependencies, safe to load more than once.
 *
 *   PrinsteyUpload.upload({ endpoint, context, files, contact, message, meta,
 *                           concurrency, signal, onProgress })
 *     -> Promise<{ submissionId, files: [{ fileId, name, size, link }], adminUrl }>
 *   PrinsteyUpload.abortBeacon(endpoint, submissionId)
 *   PrinsteyUpload.formatBytes(n) / PrinsteyUpload.formatDuration(seconds)
 *   PrinsteyUpload.UploadError { code, status, retryAt, retryable }
 *     code "file_changed" (not retryable) also carries fileName and fileIndex:
 *     that file was changed or moved on disk after it was chosen.
 *   PrinsteyUpload.busy   true while any upload runs
 *
 * onProgress receives { phase, loaded, total, fraction, bytesPerSecond,
 * etaSeconds, filesDone, filesTotal }; phase is init | uploading | completing | done.
 *
 * Retries: a part that gets an answer it cannot use (5xx, 403, …) or stalls
 * for 90 s is retried 6 times with backoff (0.6 s × 2^n, jitter, cap 20 s);
 * S3's 403 re-signs through /parts first. A request that gets no answer while
 * the browser reports being online is retried on a clock instead — until
 * nothing in the upload has moved for 5 minutes — so a router, VPN or DNS
 * blip does not throw away what was already sent. /parts and /complete do the
 * same; /init keeps a short count (nothing is sent yet). Offline waits for the
 * online event, up to 5 minutes.
 */
(function () {
  'use strict';

  if (window.PrinsteyUpload && window.PrinsteyUpload.version) return;

  const VERSION = '1.1.0';
  /* Answered failures (HTTP 5xx, 403, …) and stalls, per part. */
  const MAX_PART_RETRIES = 6;
  const BACKOFF_BASE_MS = 600;
  const BACKOFF_CAP_MS = 20000;
  const OFFLINE_WAIT_MS = 5 * 60 * 1000;
  /* Requests that fail with no answer while the browser still reports being
     online (router or WAN down on live Wi-Fi, a VPN reconnecting, DNS) are
     retried on a clock, not a count: up to this long since the part or the
     run last made progress, so a short outage never throws the upload away. */
  const NETWORK_WAIT_MS = 5 * 60 * 1000;
  const MAX_NETWORK_RETRIES = 60;
  const PROBE_TIMEOUT_MS = 10 * 1000;
  const STALL_MS = 90 * 1000;
  const REQUEST_TIMEOUT_MS = 45 * 1000;
  const INIT_RETRIES = 2;
  const PARTS_RETRIES = 3;
  const COMPLETE_RETRIES = 4;
  const PROGRESS_MIN_MS = 200;
  const SPEED_SAMPLE_MS = 500;
  const SPEED_TAU_MS = 3000;
  const DEFAULT_CONCURRENCY = 4;
  const MAX_CONCURRENCY = 8;

  /* Customer-readable fallbacks, used only when the server sends no sentence
     of its own (network failures, a proxy's HTML error page, and so on). */
  const MESSAGES = {
    network: 'We could not reach the upload server. Check your connection and try again.',
    offline: 'Your connection dropped and did not come back. Reconnect and try again.',
    upload_failed: 'Part of your file would not upload. Check your connection and try again.',
    server: 'Something went wrong on our side while receiving your file. Please try again.',
    verify_failed: 'Your file did not arrive complete. Please try again.',
    aborted: 'The upload was cancelled.',
    config: 'File uploads are not set up on this page.',
    bad_request: 'That upload could not be started. Please try again.',
    origin: 'Uploads are not accepted from this page.',
    banned: 'Uploads from this connection are blocked.',
    too_large: 'That file is too big to upload here.',
    type: 'That type of file cannot be uploaded here.',
    rate_limited: 'Too many uploads from this connection today. Please try again later.',
    not_found: 'That upload has expired. Please try again.',
    conflict: 'That upload has already finished or expired. Please try again.',
    file_changed: '“{name}” changed or moved after it was chosen, so it cannot be read. Please choose it again.'
  };

  /* false = trying the same thing again will not help. */
  const NOT_RETRYABLE = { aborted: 1, config: 1, bad_request: 1, origin: 1, banned: 1, too_large: 1, type: 1, file_changed: 1 };

  const STATUS_CODES = { 400: 'bad_request', 403: 'origin', 404: 'not_found', 409: 'conflict', 413: 'too_large', 415: 'type', 422: 'verify_failed', 429: 'rate_limited' };

  class UploadError extends Error {
    constructor(message, code, status, retryAt, retryable) {
      const c = code || 'server';
      super(message || MESSAGES[c] || MESSAGES.server);
      this.name = 'UploadError';
      this.code = c;
      this.status = typeof status === 'number' ? status : 0;
      this.retryAt = retryAt || null;
      this.retryable = typeof retryable === 'boolean' ? retryable : !NOT_RETRYABLE[c];
    }
  }

  function makeError(code, status, retryAt) {
    return new UploadError(MESSAGES[code] || MESSAGES.server, code, status, retryAt);
  }

  /* The file on disk is no longer the one that was chosen: the browser refuses
     to read the old snapshot (Chrome: ERR_UPLOAD_FILE_CHANGED), so every retry
     would fail the same way. Says which file, so the page can ask for it again. */
  function fileChangedError(file, index) {
    const name = file && file.name ? String(file.name) : 'file';
    const error = new UploadError(MESSAGES.file_changed.replace('{name}', name), 'file_changed', 0, null, false);
    error.fileName = name;
    error.fileIndex = typeof index === 'number' ? index : -1;
    return error;
  }

  /* Resolves false only when reading one byte of the file at `start` fails.
     A slow read (a cloud-synced file coming back from the network) or a
     browser without FileReader counts as readable. */
  function readable(file, start) {
    return new Promise(function (resolve) {
      if (!file || typeof file.slice !== 'function' || !(file.size > 0) || typeof FileReader !== 'function') {
        resolve(true);
        return;
      }
      let done = false;
      const finish = function (value) {
        if (done) return;
        done = true;
        clearTimeout(timer);
        resolve(value);
      };
      const timer = setTimeout(function () { finish(true); }, PROBE_TIMEOUT_MS);
      try {
        const at = Math.max(0, Math.min(Number(start) || 0, file.size - 1));
        const reader = new FileReader();
        reader.onload = function () { finish(true); };
        reader.onerror = function () { finish(false); };
        reader.onabort = function () { finish(false); };
        reader.readAsArrayBuffer(file.slice(at, at + 1));
      } catch (e) {
        finish(false);
      }
    });
  }

  /* ------------------------------------------------------------ formatting */

  function formatBytes(n) {
    const v = Number(n);
    if (!isFinite(v) || v < 0) return '';
    if (v < 1024) return Math.round(v) + ' B';
    const units = ['KB', 'MB', 'GB', 'TB'];
    let x = v / 1024;
    let i = 0;
    while (x >= 1024 && i < units.length - 1) {
      x /= 1024;
      i += 1;
    }
    return (x >= 100 ? String(Math.round(x)) : x.toFixed(1)) + ' ' + units[i];
  }

  function formatDuration(seconds) {
    const v = Number(seconds);
    if (!isFinite(v) || v < 0) return '';
    const total = Math.max(1, Math.ceil(v));
    if (total < 60) return total + ' s';
    if (total < 3600) {
      const m = Math.floor(total / 60);
      const r = total % 60;
      return m + ' min' + (r && m < 10 ? ' ' + r + ' s' : '');
    }
    const h = Math.floor(total / 3600);
    const mins = Math.floor((total % 3600) / 60);
    return h + ' h' + (mins ? ' ' + mins + ' min' : '');
  }

  /* ------------------------------------------------------------------ busy
     While anything uploads, leaving the page asks first, and a page that is
     left anyway tells the API to throw the half-sent parts away. */

  const active = new Set();

  function onBeforeUnload(event) {
    event.preventDefault();
    event.returnValue = '';
    return '';
  }

  function onPageHide() {
    active.forEach(function (run) {
      if (run.submissionId && !run.completed) abortBeacon(run.endpoint, run.submissionId);
    });
  }

  function markBusy(run) {
    active.add(run);
    if (active.size === 1) {
      window.addEventListener('beforeunload', onBeforeUnload);
      window.addEventListener('pagehide', onPageHide);
    }
  }

  function markIdle(run) {
    if (!active.delete(run)) return;
    if (active.size === 0) {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onPageHide);
    }
  }

  /* ------------------------------------------------------------------- run
     One upload. stop(reason) cancels every timer, fetch and XHR it owns and
     makes every pending wait reject with that reason. */

  class Run {
    constructor(endpoint) {
      this.endpoint = endpoint;
      this.stopped = false;
      this.reason = null;
      this.completed = false;
      this.submissionId = null;
      this.cancels = new Set();
    }

    stop(reason) {
      if (this.stopped) return;
      this.stopped = true;
      this.reason = reason;
      const list = Array.from(this.cancels);
      this.cancels.clear();
      list.forEach(function (fn) {
        try { fn(reason); } catch (e) { /* ignore */ }
      });
    }

    check() {
      if (this.stopped) throw this.reason;
    }

    /* Registers fn to run on stop; returns the unregister function. */
    onStop(fn) {
      const self = this;
      if (this.stopped) {
        fn(this.reason);
        return function () {};
      }
      this.cancels.add(fn);
      return function () { self.cancels.delete(fn); };
    }
  }

  function sleep(run, ms) {
    return new Promise(function (resolve, reject) {
      if (run.stopped) {
        reject(run.reason);
        return;
      }
      let off = null;
      const timer = setTimeout(function () {
        if (off) off();
        resolve();
      }, ms);
      off = run.onStop(function (reason) {
        clearTimeout(timer);
        reject(reason);
      });
    });
  }

  function backoff(n) {
    const base = Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * Math.pow(2, n));
    return Math.round(base * (0.5 + Math.random() * 0.5));
  }

  function isOffline() {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
  }

  /* Waits for the connection instead of burning retries on it. */
  function waitOnline(run) {
    if (!isOffline()) return Promise.resolve();
    return new Promise(function (resolve, reject) {
      if (run.stopped) {
        reject(run.reason);
        return;
      }
      let off = null;
      let timer = null;
      function done(err) {
        clearTimeout(timer);
        window.removeEventListener('online', onOnline);
        if (off) off();
        if (err) reject(err); else resolve();
      }
      function onOnline() { done(null); }
      window.addEventListener('online', onOnline);
      timer = setTimeout(function () { done(makeError('offline')); }, OFFLINE_WAIT_MS);
      off = run.onStop(function (reason) {
        clearTimeout(timer);
        window.removeEventListener('online', onOnline);
        reject(reason);
      });
    });
  }

  /* ------------------------------------------------------------------ JSON */

  function errorFromResponse(status, data) {
    const body = data && typeof data === 'object' ? data : {};
    let code = typeof body.code === 'string' && body.code ? body.code : '';
    if (!code) code = STATUS_CODES[status] || 'server';
    const message = typeof body.error === 'string' && body.error.trim() ? body.error.trim() : (MESSAGES[code] || MESSAGES.server);
    const retryAt = typeof body.retryAt === 'string' ? body.retryAt : null;
    return new UploadError(message, code, status, retryAt);
  }

  function fetchJson(run, url, body) {
    return new Promise(function (resolve, reject) {
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      let settled = false;
      let off = null;
      let timer = null;
      function finish(fn, value) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (off) off();
        fn(value);
      }
      off = run.onStop(function (reason) {
        if (controller) {
          try { controller.abort(); } catch (e) { /* ignore */ }
        }
        finish(reject, reason);
      });
      timer = setTimeout(function () {
        if (controller) {
          try { controller.abort(); } catch (e) { /* ignore */ }
        }
        finish(reject, { transient: true, status: 0, timeout: true });
      }, REQUEST_TIMEOUT_MS);
      let response = null;
      fetch(url, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
        signal: controller ? controller.signal : undefined
      }).then(function (res) {
        response = res;
        return res.text();
      }).then(function (text) {
        let data = null;
        try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
        finish(resolve, { status: response.status, ok: response.ok, data: data });
      }, function () {
        finish(reject, { transient: true, status: 0 });
      });
    });
  }

  function pageOrigin() {
    try {
      return window.location && window.location.origin ? String(window.location.origin) : '';
    } catch (e) {
      return '';
    }
  }

  /* POST with retries on network trouble and 5xx. 4xx is final.
     `networkBudgetMs` (for calls made once files are already uploading): a
     request that gets no answer is retried for that long instead of `retries`
     times, so a short outage does not throw away what was sent. */
  async function postJson(run, url, body, retries, label, networkBudgetMs) {
    let failures = 0;
    let networkSince = 0;
    let networkTries = 0;
    for (;;) {
      run.check();
      await waitOnline(run);
      let result;
      try {
        result = await fetchJson(run, url, body);
      } catch (err) {
        if (run.stopped) throw run.reason;
        if (err instanceof UploadError) throw err;
        if (isOffline()) continue;
        if (networkBudgetMs > 0) {
          const now = Date.now();
          if (!networkSince) networkSince = now;
          if (now - networkSince < networkBudgetMs) {
            networkTries += 1;
            console.warn('[PrinsteyUpload] ' + label + ' failed to connect; retrying (' + Math.round((now - networkSince) / 1000) + ' s so far)');
            await sleep(run, backoff(networkTries - 1));
            continue;
          }
        } else if (failures < retries) {
          failures += 1;
          console.warn('[PrinsteyUpload] ' + label + ' failed to connect; retrying (' + failures + '/' + retries + ')');
          await sleep(run, backoff(failures - 1));
          continue;
        }
        if (label === 'init') {
          // A browser blocks a request its CORS preflight refused, and the
          // page sees that exactly like a dead connection: say so for whoever
          // is debugging, since the customer only gets "check your connection".
          console.warn('[PrinsteyUpload] init never reached ' + url + ' while the browser reports being online. ' +
            'If this page (' + (pageOrigin() || 'this origin') + ') is not in the API\'s STORE_UPLOAD_ORIGINS and the ' +
            'bucket\'s CORS AllowedOrigins, the browser blocks the request and it looks like this.');
        }
        throw makeError('network');
      }
      networkSince = 0;
      networkTries = 0;
      if (result.ok) {
        if (result.data && typeof result.data === 'object') return result.data;
        throw makeError('server', result.status);
      }
      const error = errorFromResponse(result.status, result.data);
      const transient = result.status >= 500 || result.status === 408 || result.status === 0;
      if (transient && failures < retries) {
        failures += 1;
        console.warn('[PrinsteyUpload] ' + label + ' answered HTTP ' + result.status + '; retrying (' + failures + '/' + retries + ')');
        await sleep(run, backoff(failures - 1));
        continue;
      }
      throw error;
    }
  }

  /* ----------------------------------------------------------------- parts */

  /* Only headers the response exposes are listed, so this never trips the
     browser's "refused to get unsafe header" console error. */
  function readEtag(xhr) {
    try {
      const all = xhr.getAllResponseHeaders() || '';
      const match = /^etag:\s*(.+?)\s*$/im.exec(all);
      return match ? match[1] : null;
    } catch (e) {
      return null;
    }
  }

  function putPart(run, task, onBytes) {
    return new Promise(function (resolve, reject) {
      if (run.stopped) {
        reject(run.reason);
        return;
      }
      const xhr = new XMLHttpRequest();
      let settled = false;
      let lastProgress = Date.now();
      let off = null;
      const watchdog = setInterval(function () {
        if (Date.now() - lastProgress > STALL_MS) {
          settle();
          try { xhr.abort(); } catch (e) { /* ignore */ }
          reject({ transient: true, status: 0, stalled: true });
        }
      }, 5000);
      function settle() {
        settled = true;
        clearInterval(watchdog);
        if (off) off();
      }
      off = run.onStop(function (reason) {
        if (settled) return;
        settle();
        try { xhr.abort(); } catch (e) { /* ignore */ }
        reject(reason);
      });
      xhr.open('PUT', task.url, true);
      xhr.upload.onprogress = function (event) {
        lastProgress = Date.now();
        if (!settled) onBytes(Math.min(event.loaded, task.size));
      };
      xhr.onload = function () {
        if (settled) return;
        settle();
        if (xhr.status >= 200 && xhr.status < 300) resolve(readEtag(xhr));
        else reject({ transient: true, status: xhr.status });
      };
      xhr.onerror = function () {
        if (settled) return;
        settle();
        reject({ transient: true, status: 0 });
      };
      xhr.onabort = function () {
        if (settled) return;
        settle();
        reject(run.stopped ? run.reason : { transient: true, status: 0 });
      };
      try {
        xhr.send(task.file.slice(task.start, task.end));
      } catch (e) {
        if (!settled) {
          settle();
          reject({ transient: true, status: 0 });
        }
      }
    });
  }

  /* S3 said 403: the URLs for this file expired. One /parts call re-signs
     every unfinished part of it; concurrent 403s share that call. */
  function resign(run, entry) {
    if (entry.resigning) return entry.resigning;
    const partNumbers = entry.tasks.filter(function (t) { return !t.done; }).map(function (t) { return t.partNumber; });
    entry.resigning = postJson(run, run.endpoint + '/parts', {
      submissionId: run.submissionId,
      fileId: entry.fileId,
      partNumbers: partNumbers
    }, PARTS_RETRIES, 'parts', NETWORK_WAIT_MS).then(function (data) {
      const parts = data && Array.isArray(data.parts) ? data.parts : [];
      entry.generation += 1;
      parts.forEach(function (p) {
        const task = entry.byNumber[p && p.partNumber];
        if (task && typeof p.url === 'string' && p.url) {
          task.url = p.url;
          task.generation = entry.generation;
        }
      });
      entry.resigning = null;
    }, function (err) {
      entry.resigning = null;
      throw err;
    });
    return entry.resigning;
  }

  async function runTask(run, task, meter) {
    let failures = 0;       // answered failures and stalls: MAX_PART_RETRIES
    let networkSince = 0;   // first unanswered failure since this part last moved
    let networkTries = 0;
    let networkTotal = 0;
    for (;;) {
      run.check();
      await waitOnline(run);
      const generation = task.generation;
      let moved = false;
      try {
        const etag = await putPart(run, task, function (bytes) {
          if (bytes > 0) moved = true;
          meter.inflight(task, bytes);
        });
        meter.partDone(task, etag);
        run.lastPartAt = Date.now();
        return;
      } catch (err) {
        if (run.stopped) throw run.reason;
        if (err instanceof UploadError) throw err;
        meter.inflight(task, 0);
        if (isOffline()) continue;
        if (!(err && err.status) && !(err && err.stalled)) {
          // No answer at all. A file changed on disk fails like this on every
          // attempt, so check that first; otherwise wait the outage out.
          if (!(await readable(task.file, task.start))) throw fileChangedError(task.file, task.entry.index);
          run.check();
          const now = Date.now();
          if (moved || !networkSince) {
            networkSince = now;
            networkTries = 0;
          }
          networkTotal += 1;
          const lastProgress = Math.max(networkSince, run.lastPartAt || 0);
          if (now - lastProgress >= NETWORK_WAIT_MS || networkTotal > MAX_NETWORK_RETRIES) {
            throw new UploadError(MESSAGES.upload_failed, 'upload_failed', 0);
          }
          networkTries += 1;
          console.warn('[PrinsteyUpload] part ' + task.partNumber + ' of ' + task.entry.name +
            ' failed (network); retrying (' + Math.round((now - networkSince) / 1000) + ' s so far)');
          await sleep(run, backoff(networkTries - 1));
          continue;
        }
        failures += 1;
        if (failures > MAX_PART_RETRIES) {
          throw new UploadError(MESSAGES.upload_failed, 'upload_failed', err && err.status ? err.status : 0);
        }
        const status = err && err.status ? err.status : 0;
        console.warn('[PrinsteyUpload] part ' + task.partNumber + ' of ' + task.entry.name + ' failed' +
          (status ? ' (HTTP ' + status + ')' : ' (stalled)') +
          '; retry ' + failures + '/' + MAX_PART_RETRIES);
        if (status === 403) {
          if (task.entry.generation === generation) await resign(run, task.entry);
          continue;
        }
        await sleep(run, backoff(failures - 1));
      }
    }
  }

  function runPool(run, tasks, concurrency) {
    let next = 0;
    function worker() {
      return (async function () {
        while (next < tasks.length) {
          run.check();
          const task = tasks[next];
          next += 1;
          await runTask(run, task, run.meter);
        }
      })().catch(function (err) {
        run.stop(err);
        throw err;
      });
    }
    const workers = [];
    const n = Math.max(1, Math.min(concurrency, tasks.length));
    for (let i = 0; i < n; i += 1) workers.push(worker());
    return Promise.all(workers);
  }

  /* -------------------------------------------------------------- progress */

  function createMeter(total, filesTotal, onProgress) {
    const inflight = new Map();
    const meter = {
      phase: 'init',
      total: total,
      confirmed: 0,
      shown: 0,
      speed: null,
      lastSampleAt: 0,
      lastSampleBytes: 0,
      lastEmit: 0,
      filesDone: 0,
      filesTotal: filesTotal,
      ticker: null
    };

    function current() {
      let sum = meter.confirmed;
      inflight.forEach(function (v) { sum += v; });
      return Math.min(meter.total, sum);
    }

    function sample(now) {
      const dt = now - meter.lastSampleAt;
      if (dt < SPEED_SAMPLE_MS) return;
      const rate = Math.max(0, meter.shown - meter.lastSampleBytes) / (dt / 1000);
      if (meter.speed === null) meter.speed = rate;
      else meter.speed += (1 - Math.exp(-dt / SPEED_TAU_MS)) * (rate - meter.speed);
      meter.lastSampleAt = now;
      meter.lastSampleBytes = meter.shown;
    }

    function emit(force) {
      const now = Date.now();
      // Never report going backwards: a retried part holds the bar still.
      meter.shown = Math.max(meter.shown, current());
      if (meter.phase === 'uploading') sample(now);
      if (!force && now - meter.lastEmit < PROGRESS_MIN_MS) return;
      meter.lastEmit = now;
      if (typeof onProgress !== 'function') return;
      const speed = meter.speed && meter.speed > 0 ? meter.speed : 0;
      const remaining = Math.max(0, meter.total - meter.shown);
      const done = meter.phase === 'done';
      try {
        onProgress({
          phase: meter.phase,
          loaded: done ? meter.total : meter.shown,
          total: meter.total,
          fraction: done ? 1 : (meter.total > 0 ? Math.min(1, meter.shown / meter.total) : 0),
          bytesPerSecond: speed,
          etaSeconds: done ? 0 : (speed > 0 ? remaining / speed : null),
          filesDone: meter.filesDone,
          filesTotal: meter.filesTotal
        });
      } catch (e) {
        /* a broken callback must not break the upload */
      }
    }

    meter.setPhase = function (phase) {
      meter.phase = phase;
      if (phase === 'uploading') {
        meter.lastSampleAt = Date.now();
        meter.lastSampleBytes = meter.shown;
        if (!meter.ticker) meter.ticker = setInterval(function () { emit(false); }, SPEED_SAMPLE_MS);
      } else if (meter.ticker) {
        clearInterval(meter.ticker);
        meter.ticker = null;
      }
      emit(true);
    };
    meter.inflight = function (task, bytes) {
      inflight.set(task, bytes);
      emit(false);
    };
    meter.partDone = function (task, etag) {
      inflight.delete(task);
      task.done = true;
      task.etag = etag;
      meter.confirmed += task.size;
      const entry = task.entry;
      entry.remaining -= 1;
      if (entry.remaining === 0) meter.filesDone += 1;
      emit(entry.remaining === 0);
    };
    meter.stop = function () {
      if (meter.ticker) clearInterval(meter.ticker);
      meter.ticker = null;
    };
    return meter;
  }

  /* ---------------------------------------------------------------- upload */

  function cleanEndpoint(endpoint) {
    return String(endpoint || '').trim().replace(/\/+$/, '');
  }

  function buildEntries(files, init) {
    const list = Array.isArray(init.files) ? init.files : [];
    if (list.length !== files.length) throw makeError('server');
    const entries = [];
    list.forEach(function (item, position) {
      const index = item && typeof item.index === 'number' ? item.index : position;
      const file = files[index];
      if (!item || !file || typeof item.fileId !== 'string' || !item.fileId) throw makeError('server');
      const size = file.size;
      const partSize = Number(item.partSize);
      const parts = Array.isArray(item.parts) ? item.parts : [];
      const expected = size > 0 ? Math.ceil(size / partSize) : 1;
      if (!(partSize > 0) || parts.length !== expected) throw makeError('server');
      const entry = {
        index: index,
        fileId: item.fileId,
        name: file.name,
        size: size,
        partSize: partSize,
        tasks: [],
        byNumber: {},
        remaining: parts.length,
        generation: 0,
        resigning: null
      };
      parts.forEach(function (p) {
        const n = Number(p && p.partNumber);
        if (!(n >= 1 && n <= expected) || typeof p.url !== 'string' || !p.url || entry.byNumber[n]) throw makeError('server');
        const start = (n - 1) * partSize;
        const end = Math.min(size, n * partSize);
        const task = {
          entry: entry,
          file: file,
          partNumber: n,
          url: p.url,
          start: start,
          end: end,
          size: end - start,
          generation: 0,
          done: false,
          etag: null
        };
        entry.byNumber[n] = task;
        entry.tasks.push(task);
      });
      entry.tasks.sort(function (a, b) { return a.partNumber - b.partNumber; });
      entries.push(entry);
    });
    return entries;
  }

  async function upload(options) {
    const opts = options || {};
    const endpoint = cleanEndpoint(opts.endpoint);
    const files = Array.prototype.slice.call(opts.files || []);
    if (!endpoint || typeof fetch !== 'function' || typeof XMLHttpRequest !== 'function') throw makeError('config');
    if (!files.length || files.some(function (f) { return !f || typeof f.size !== 'number' || typeof f.slice !== 'function'; })) {
      throw makeError('bad_request');
    }
    const signal = opts.signal || null;
    if (signal && signal.aborted) throw makeError('aborted');

    const run = new Run(endpoint);
    const total = files.reduce(function (sum, f) { return sum + f.size; }, 0);
    const meter = createMeter(total, files.length, opts.onProgress);
    run.meter = meter;
    let concurrency = Math.round(Number(opts.concurrency));
    if (!(concurrency >= 1)) concurrency = DEFAULT_CONCURRENCY;
    concurrency = Math.min(MAX_CONCURRENCY, concurrency);

    const onAbort = function () { run.stop(makeError('aborted')); };
    if (signal) signal.addEventListener('abort', onAbort);
    markBusy(run);

    try {
      meter.setPhase('init');
      // A file changed on disk since it was chosen can never be sent: say so
      // before /init, so no submission is opened and no daily slot is used.
      const readability = await Promise.all(files.map(function (f) { return readable(f, 0); }));
      run.check();
      const unreadable = readability.indexOf(false);
      if (unreadable !== -1) throw fileChangedError(files[unreadable], unreadable);
      const fingerprints = Array.isArray(opts.fingerprints) ? opts.fingerprints : [];
      const init = await postJson(run, endpoint + '/init', {
        context: opts.context,
        files: files.map(function (f, i) {
          const item = { name: f.name || 'file', size: f.size, type: f.type || 'application/octet-stream' };
          if (typeof fingerprints[i] === 'string' && fingerprints[i]) item.sha256 = fingerprints[i];
          return item;
        }),
        contact: opts.contact || undefined,
        message: typeof opts.message === 'string' && opts.message ? opts.message.slice(0, 4000) : undefined,
        meta: opts.meta || undefined
      }, INIT_RETRIES, 'init');
      if (!init || typeof init.submissionId !== 'string' || !init.submissionId) throw makeError('server');
      run.submissionId = init.submissionId;
      run.check();

      const entries = buildEntries(files, init);
      const tasks = [];
      entries.forEach(function (entry) { entry.tasks.forEach(function (t) { tasks.push(t); }); });

      meter.setPhase('uploading');
      await runPool(run, tasks, concurrency);
      run.check();

      meter.setPhase('completing');
      const done = await postJson(run, endpoint + '/complete', {
        submissionId: run.submissionId,
        files: entries.map(function (entry) {
          const item = { fileId: entry.fileId };
          if (entry.tasks.every(function (t) { return !!t.etag; })) {
            item.parts = entry.tasks.map(function (t) { return { partNumber: t.partNumber, etag: t.etag }; });
          }
          return item;
        })
      }, COMPLETE_RETRIES, 'complete', NETWORK_WAIT_MS);
      if (!done || done.ok !== true || !Array.isArray(done.files)) throw makeError('server');
      run.check();
      run.completed = true;

      const byId = {};
      entries.forEach(function (entry) { byId[entry.fileId] = entry; });
      const out = done.files.map(function (f) {
        const entry = byId[f && f.fileId] || {};
        return {
          fileId: String(f && f.fileId || ''),
          name: typeof f.name === 'string' ? f.name : (entry.name || ''),
          size: typeof f.size === 'number' ? f.size : (entry.size || 0),
          link: typeof f.link === 'string' ? f.link : ''
        };
      });
      meter.filesDone = files.length;
      meter.setPhase('done');
      return {
        submissionId: typeof done.submissionId === 'string' ? done.submissionId : run.submissionId,
        files: out,
        adminUrl: typeof done.adminUrl === 'string' ? done.adminUrl : ''
      };
    } catch (err) {
      const error = err instanceof UploadError ? err : (run.reason instanceof UploadError ? run.reason : makeError('server'));
      run.stop(error);
      if (run.submissionId && !run.completed) abortBeacon(endpoint, run.submissionId);
      throw error;
    } finally {
      meter.stop();
      if (signal) signal.removeEventListener('abort', onAbort);
      // Before the caller's then(): a native form submit right after must not
      // meet the leave-page prompt.
      markIdle(run);
    }
  }

  function abortBeacon(endpoint, submissionId) {
    const ep = cleanEndpoint(endpoint);
    if (!ep || !submissionId) return false;
    const url = ep + '/abort';
    const body = JSON.stringify({ submissionId: String(submissionId) });
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(url, body)) return true;
    } catch (e) { /* fall through to fetch */ }
    try {
      fetch(url, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        keepalive: true,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: body
      }).catch(function () {});
      return true;
    } catch (e) {
      return false;
    }
  }

  const api = {
    version: VERSION,
    upload: upload,
    abortBeacon: abortBeacon,
    formatBytes: formatBytes,
    formatDuration: formatDuration,
    UploadError: UploadError
  };
  Object.defineProperty(api, 'busy', {
    enumerable: true,
    get: function () { return active.size > 0; }
  });
  window.PrinsteyUpload = api;
})();
